/**
 * Credits Purchase Modal
 * Displays credit packages and handles Stripe checkout for purchases
 */

import { useState } from 'react';
import { useAuth } from '@/context';
import styles from './CreditsModal.module.css';

interface CreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseComplete?: () => void; // Called after successful credit purchase (currently redirects to Stripe, so not used yet)
}

const PACKAGES = [
  { id: '1', credits: 10, price: 1, popular: false },
  { id: '5', credits: 50, price: 5, popular: true },
  { id: '10', credits: 100, price: 10, popular: false },
];

export function CreditsModal({
  isOpen,
  onClose,
  onPurchaseComplete: _onPurchaseComplete,
}: CreditsModalProps) {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handlePurchase = async (packageId: string) => {
    if (!user || !session) {
      setError('You must be signed in to purchase credits');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get authorization token
      const token = session.access_token;

      const response = await fetch('/api/create-credit-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          packageId,
          userId: user.id,
          userEmail: user.email,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      console.error('[CreditsModal] Purchase error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="credits-modal-title"
      >
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close credits modal"
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6 6L18 18M6 18L18 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <h2 id="credits-modal-title" className={styles.title}>
          Purchase Credits
        </h2>

        <div className={styles.freeCreditsCallout}>
          <strong>New users get 5 free credits</strong> upon email verification or Google sign-in!
        </div>

        <p className={styles.description}>
          Credits let you generate AI designs and pay for products. $1 = 10 credits.
        </p>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <div className={styles.packages}>
          {PACKAGES.map((pkg) => (
            <div key={pkg.id} className={`${styles.package} ${pkg.popular ? styles.popular : ''}`}>
              {pkg.popular && <span className={styles.popularBadge}>Most Popular</span>}
              <div className={styles.packageHeader}>
                <span className={styles.credits}>{pkg.credits}</span>
                <span className={styles.creditsLabel}>credits</span>
              </div>
              <div className={styles.price}>${pkg.price}.00</div>
              <button
                className={styles.purchaseButton}
                onClick={() => handlePurchase(pkg.id)}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Purchase'}
              </button>
            </div>
          ))}
        </div>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            1 credit = 1 AI design generation. Credits never expire.
          </p>
        </div>
      </div>
    </div>
  );
}
